import { ClickAwayListener } from '@material-ui/core';
import { nanoid } from 'nanoid';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { CheckedImage } from 'product_modules/static/images';
import { ArrowDownIcon, CloseIcon, SearchIcon } from 'static/images';
import styled from 'types/AppTheme';

const DropdownMainWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 24px;
`;

const DropdownLabel = styled.label<{ required?: boolean }>`
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  color: #0f2342;

  & > span {
    padding: 0px 5px;
    font-size: 14px;
    color: #f76565;
    font-weight: 500;
  }
`;

const DropdownWrapper = styled.div`
  position: relative;
`;

const DropdownInputLabel = styled.label<{ hasError: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border: 1px solid #e7e9ec;
  border-radius: 4px;
  height: 52px;
  cursor: pointer;
  width: 100%;
  background-color: #ffffff;
  gap: 10px;

  &:hover {
    border-color: #cfd3d9;
  }

  &:has(input:focus) {
    border-color: #a5d5fc;
  }

  ${({ hasError }) =>
    hasError &&
    `
    border-color: #f76565;  
    &:hover,
    &:has(input:focus) {
      border-color: #f76565;
    }
  `}
`;

const DropdownInputWrapper = styled.div`
  display: flex;
  flex: 1 1 auto;
`;

const DropdownInput = styled.input`
  font-family: 'Montserrat', 'sans-serif';
  flex: 1 1 auto;
  width: 100%;
  padding: 0;
  outline: none;
  border: none;
  font-size: 14px;
  color: #0f2342;
  line-height: 20px;
  font-weight: 500;
  cursor: pointer;
  text-overflow: ellipsis;

  &::placeholder {
    color: #9fa7b3;
  }
`;

const DropdownIconsWrapper = styled.div<{ isCloseIconShown: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;

  & > svg:first-child {
    display: ${({ isCloseIconShown }) => (isCloseIconShown ? 'block' : 'none')};
  }

  & > svg {
    width: 24px;
    height: 24px;
  }
`;

const DropdownListWrapper = styled.div`
  position: absolute;
  top: 100%;
  display: flex;
  flex-direction: column;
  max-height: 300px;
  overflow-y: auto;
  background: #ffffff;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  width: 100%;
  z-index: 1000;
`;

const DropdownListItem = styled.div`
  padding: 10px 16px 10px 20px;
  cursor: pointer;
  width: 100%;
  font-family: 'Montserrat', 'sans-serif';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundHoverAccentColor};
  }
`;

const DropdownErrorMessage = styled.span`
  font-size: 14px;

  font-weight: 500;
  line-height: 20px;
  font-family: 'Montserrat', 'sans-serif';
  color: #f76565;
`;

interface IDropdownProps {
  label: string;
  options: string[];
  value: string;
  onSelect: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  required?: boolean;
  placeholder?: string;
  errorMessage?: string;
}

const Dropdown = ({
  value,
  options,
  label,
  onSelect,
  onBlur,
  onFocus,
  placeholder,
  required = false,
  errorMessage,
}: IDropdownProps) => {
  const [inputValue, setInputValue] = useState<string>(value);
  const [selectedItem, setSelectedItem] = useState<string>(value);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Refs
  const uniqueId = useRef(nanoid());
  const inputRef = useRef<HTMLInputElement>(null);
  const prevInputValue = useRef<string>('');
  const prevSelectedItem = useRef<string>('');

  useEffect(() => {
    const { current } = inputRef;
    if (current) {
      current.autocomplete = uniqueId.current as typeof current.autocomplete;
      current.id = uniqueId.current;
      current.name = uniqueId.current;
    }
  }, []);

  const handleOnInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (isOpen) {
      event.currentTarget.blur();
    }
  };

  const handleOnClickAway = () => {
    if (isOpen) {
      setInputValue(prevInputValue.current);
      setSelectedItem(prevSelectedItem.current);
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      if (isOpen) {
        inputRef.current.focus();
        onFocus?.();
      }
      if (!isOpen) {
        inputRef.current.blur();
        onBlur?.();
      }
    }
  }, [isOpen]);

  const handleToggleList = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen((prevIsOpen) => {
      if (prevIsOpen) {
        // Close the list
        setInputValue(prevInputValue.current);
        setSelectedItem(prevSelectedItem.current);
        return false;
      }

      prevInputValue.current = inputValue;
      prevSelectedItem.current = selectedItem;
      setInputValue('');
      setSelectedItem('');
      return true;
    });
  };

  const handleSelectItem = (selected: string) => {
    onSelect(selected);
    setSelectedItem(selected);
    setInputValue(selected);
    setIsOpen(false);
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleClearIconClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setInputValue('');
    setSelectedItem('');
    prevInputValue.current = '';
    prevSelectedItem.current = '';
    onSelect('');
  };

  const optionsToRender = useMemo(() => {
    const regex = new RegExp(inputValue.toLowerCase().trim(), 'gim');

    return options.filter((option) => option.toLowerCase().trim().match(regex));
  }, [inputValue]);

  return (
    <ClickAwayListener onClickAway={handleOnClickAway}>
      <DropdownMainWrapper>
        <DropdownLabel htmlFor={uniqueId.current}>
          {label}
          {required && <span>*</span>}
        </DropdownLabel>
        <DropdownWrapper onClick={handleToggleList}>
          <DropdownInputLabel hasError={!!errorMessage}>
            <DropdownInputWrapper>
              <DropdownInput
                onChange={handleOnChange}
                id={uniqueId.current}
                type="text"
                value={inputValue}
                name={uniqueId.current}
                autoComplete={uniqueId.current}
                placeholder={placeholder}
                onClick={handleOnInputClick}
                ref={inputRef}
              />
            </DropdownInputWrapper>
            <DropdownIconsWrapper isCloseIconShown={!!inputValue}>
              <CloseIcon onClick={handleClearIconClick} />
              {isOpen ? <SearchIcon /> : <ArrowDownIcon />}
            </DropdownIconsWrapper>
          </DropdownInputLabel>
          {isOpen && (
            <DropdownListWrapper>
              {optionsToRender.map((option) => (
                <DropdownListItem
                  key={option}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleSelectItem(option);
                  }}
                >
                  <div>{option}</div>
                  {(selectedItem === option || prevSelectedItem.current === option) && (
                    <CheckedImage />
                  )}
                </DropdownListItem>
              ))}
            </DropdownListWrapper>
          )}
        </DropdownWrapper>
        {errorMessage && <DropdownErrorMessage>{errorMessage}</DropdownErrorMessage>}
      </DropdownMainWrapper>
    </ClickAwayListener>
  );
};

export default React.memo(Dropdown);
